import React, { useContext, useEffect, useState } from "react";
import {
    Avatar,
    Box,
    CssBaseline,
    Divider,
    IconButton,
    List,
    Menu,
    MenuItem,
    Stack,
    ThemeProvider,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import MuiAppBar from "@mui/material/AppBar";
import MuiDrawer from "@mui/material/Drawer";
import { styled, useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import MenuContent from "../components/MenuContent";
import SideMenuMobile from "../components/SideMenuMobile";
import DarkModeSwitch from "../components/DarkModeSwitch";
import LanguageSwitch from "../components/LanguageSwitch";
import PimsBranding from "../components/PimsBranding";
import Notification from "../components/Notification";
import SupplyChainBackdrop from "../components/SupplyChainBackdrop";
import { LanguageContext } from "../components/LanguageContext";
import { ReactComponent as Logo } from "../assets/KaproSCMIcon.svg";
import { darkTheme, lightTheme } from "../theme";
import { logout } from "../apis/authApi";
import { clearAuth, getUserId } from "../apis/auth";

const DRAWER_WIDTH = 268;
const COLLAPSED_WIDTH = 76;

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "open" && prop !== "isMobile",
})(({ theme, open, isMobile }) => ({
    zIndex: theme.zIndex.drawer + 1,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(isMobile
        ? {}
        : open
        ? {
              marginLeft: DRAWER_WIDTH,
              width: `calc(100% - ${DRAWER_WIDTH}px)`,
              transition: theme.transitions.create(["width", "margin"], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
              }),
          }
        : {
              marginLeft: COLLAPSED_WIDTH,
              width: `calc(100% - ${COLLAPSED_WIDTH}px)`,
          }),
}));

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    "& .MuiDrawer-paper": {
        position: "fixed",
        whiteSpace: "nowrap",
        width: DRAWER_WIDTH,
        height: "100vh",
        top: 0,
        left: 0,
        overflowY: "hidden",
        backgroundImage: "none",
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        ...(!open && {
            overflowX: "hidden",
            width: COLLAPSED_WIDTH,
            transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
        }),
    },
}));

const Layout = ({ children }) => {
    const navigate = useNavigate();
    const { translations } = useContext(LanguageContext);
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

    const [mode, setMode] = useState(() =>
        localStorage.getItem("theme") === "dark" ? "dark" : "light"
    );
    const theme = mode === "dark" ? darkTheme : lightTheme;

    const [open, setOpen] = useState(!isMobile);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const [notifMessage, setNotifMessage] = useState(null);
    const [notifType, setNotifType] = useState(null);
    const [notifOpen, setNotifOpen] = useState(false);

    useEffect(() => {
        if (isMobile) {
            setOpen(false);
        } else {
            setMobileOpen(false);
        }
    }, [isMobile]);

    const toggleDrawer = () => {
        if (isMobile) setMobileOpen((v) => !v);
        else setOpen((v) => !v);
    };

    const handleToggleTheme = () => {
        setMode((prev) => {
            const next = prev === "dark" ? "light" : "dark";
            localStorage.setItem("theme", next);
            return next;
        });
    };

    const handleProfileMenuOpen = (e) => setAnchorEl(e.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const logoutMutation = useMutation({
        mutationFn: (formData) => logout(formData),
        onSuccess: (response) => {
            setNotifType(1);
            setNotifMessage(response.data.message);
            setNotifOpen(true);
            setTimeout(() => {
                clearAuth();
                navigate("/", { replace: true });
            }, 1500);
        },
        onError: (error) => {
            // Even if the server-side revoke fails (e.g. token already expired),
            // clear the client-side session so the user actually logs out.
            clearAuth();
            setNotifType(2);
            setNotifMessage(error.response?.data?.message || translations.logoutFailed || "Logout failed");
            setNotifOpen(true);
            setTimeout(() => navigate("/", { replace: true }), 1200);
        },
    });

    const handleLogout = () => {
        handleMenuClose();
        const formData = new FormData();
        formData.append("id", getUserId());
        logoutMutation.mutateAsync(formData).catch(() => {});
    };

    const renderProfileMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            slotProps={{ paper: { sx: { mt: 1, minWidth: 220 } } }}
        >
            <MenuItem
                onClick={() => {
                    navigate("/profile");
                    handleMenuClose();
                }}
            >
                <PersonOutlineRoundedIcon sx={{ mr: 1.5, color: "text.secondary" }} fontSize="small" />
                <Typography variant="body2" fontWeight={500}>
                    {translations.menuProfile || "Profile"}
                </Typography>
            </MenuItem>
            <MenuItem
                onClick={() => {
                    navigate("/settings");
                    handleMenuClose();
                }}
            >
                <SettingsOutlinedIcon sx={{ mr: 1.5, color: "text.secondary" }} fontSize="small" />
                <Typography variant="body2" fontWeight={500}>
                    {translations.menuSettings || "Settings"}
                </Typography>
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={handleLogout}>
                <LogoutRoundedIcon sx={{ mr: 1.5, color: "error.main" }} fontSize="small" />
                <Typography variant="body2" fontWeight={500} color="error.main">
                    {translations.menuLogout || "Logout"}
                </Typography>
            </MenuItem>
        </Menu>
    );

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Notification open={notifOpen} message={notifMessage} setOpen={setNotifOpen} type={notifType} />
            <Box sx={{ display: "flex", minHeight: "100vh" }}>
                <AppBar position="fixed" open={open} isMobile={isMobile}>
                    <Toolbar sx={{ minHeight: { xs: 64, sm: 68 }, px: { xs: 2, sm: 3 } }}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="toggle drawer"
                            onClick={toggleDrawer}
                            sx={{ mr: 2, ...(!isMobile && open && { display: "none" }) }}
                        >
                            <MenuRoundedIcon />
                        </IconButton>

                        <Typography component="h1" variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 600 }}>
                            {translations.appTitle || "KaproSCM Administration"}
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Tooltip title={translations.toggleTheme || "Toggle theme"}>
                                <Box>
                                    <DarkModeSwitch onChange={handleToggleTheme} checked={mode === "dark"} />
                                </Box>
                            </Tooltip>
                            <LanguageSwitch />
                            <Tooltip title={translations.accountMenu || "Account"}>
                                <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0.5 }}>
                                    <Avatar
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            bgcolor: "primary.main",
                                            color: "primary.contrastText",
                                        }}
                                    >
                                        <PersonOutlineRoundedIcon fontSize="small" />
                                    </Avatar>
                                </IconButton>
                            </Tooltip>
                            {renderProfileMenu}
                        </Stack>
                    </Toolbar>
                </AppBar>

                {isMobile ? (
                    <SideMenuMobile open={mobileOpen} onClose={() => setMobileOpen(false)} />
                ) : (
                    <Drawer variant="permanent" open={open}>
                        <Toolbar
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: open ? "space-between" : "center",
                                px: open ? 2 : 1,
                                minHeight: { xs: 64, sm: 68 },
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                                sx={{
                                    overflow: "hidden",
                                    flexGrow: 1,
                                    justifyContent: open ? "flex-start" : "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Logo style={{ width: "100%", height: "100%" }} />
                                </Box>
                                {open && (
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 700, letterSpacing: "0.2px" }}
                                        noWrap
                                    >
                                        {translations.appName || "KaproSCM"}
                                    </Typography>
                                )}
                            </Stack>
                            {open && (
                                <IconButton onClick={toggleDrawer} edge="end" size="small">
                                    <ChevronLeftRoundedIcon />
                                </IconButton>
                            )}
                        </Toolbar>

                        <Divider />

                        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", px: 1, py: 1.5 }}>
                            <List
                                component="nav"
                                sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                            >
                                <MenuContent collapsed={!open} />
                            </List>
                        </Box>

                        {open && (
                            <>
                                <Divider />
                                <PimsBranding />
                            </>
                        )}
                    </Drawer>
                )}

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        bgcolor: "background.default",
                        minHeight: "100vh",
                        marginLeft: isMobile
                            ? 0
                            : open
                            ? `${DRAWER_WIDTH}px`
                            : `${COLLAPSED_WIDTH}px`,
                        transition: "margin 0.3s ease",
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    {/* Animated supply-chain backdrop behind every page */}
                    <SupplyChainBackdrop
                        opacity={mode === "dark" ? 0.22 : 0.32}
                        animate
                    />
                    <Toolbar sx={{ minHeight: { xs: 64, sm: 68 }, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, p: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}>
                        {children}
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default Layout;